import React, { useState, useCallback } from 'react';
import { Box, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { vortxTypography } from '@vortx/shared-ui';
import { useAuth } from '@vortx/shared-ui';
import { useParams } from 'react-router-dom';

import TopologyBrowser from './TopologyBrowser';
import SimulationConfig from './SimulationConfig';
import ResultsView from './ResultsView';
import { usePhdTopologies, usePhdHistory } from '../../hooks';
import { accentColor } from './utils/topologyColors';

const STEPS = [
    { id: 'topologies', label: '1. Topologies' },
    { id: 'simulation', label: '2. Simulation' },
    { id: 'results', label: '3. Results' },
];

/**
 * AcDcSystemsTab — Combines the Topology → Simulation → Results workflow
 * into a single tab with internal step navigation.
 */
export default function AcDcSystemsTab() {
    const { projectId } = useParams();
    const { currentUser } = useAuth();
    const [step, setStep] = useState('topologies');

    const topologyState = usePhdTopologies();
    const history = usePhdHistory(currentUser, projectId);

    // Navigate to simulation with selected topologies
    const handleRunSimulation = useCallback((topologyOrTopologies) => {
        const topologies = Array.isArray(topologyOrTopologies)
            ? topologyOrTopologies
            : [topologyOrTopologies];
        topologyState.setSelectedTopologies(topologies);
        setStep('simulation');
    }, [topologyState]);

    // Handle simulation completion
    const handleSimulationComplete = useCallback(async (result, config, isComparison = false) => {
        if (isComparison) {
            history.setComparisonResults(result);
            history.setSimulationResult(null);
        } else {
            history.setSimulationResult(result);
            history.setComparisonResults(null);
            await history.handleSaveSimulation(config, result, topologyState.selectedTopologies[0]);
        }
        setStep('results');
    }, [topologyState.selectedTopologies, history]);

    // Load a saved simulation
    const handleLoadSimulation = useCallback(async (simulation) => {
        const data = await history.handleLoadSimulation(simulation);
        if (simulation.topology) {
            topologyState.setSelectedTopologies([simulation.topology]);
        } else if (data?.topology_id) {
            topologyState.setSelectedTopologies([{
                id: data.topology_id,
                name: data.topology_name,
            }]);
        }
    }, [history, topologyState]);

    // Determine which steps are reachable (can click to navigate)
    const canGoTo = (targetStep) => {
        if (targetStep === 'topologies') return true;
        if (targetStep === 'simulation') return topologyState.selectedTopologies.length > 0;
        if (targetStep === 'results') return !!(history.simulationResult || history.comparisonResults);
        return false;
    };

    const renderContent = () => {
        switch (step) {
            case 'topologies':
                return (
                    <TopologyBrowser
                        selectedTopologies={topologyState.selectedTopologies}
                        onTopologySelect={topologyState.selectTopology}
                        onContinueToSimulation={handleRunSimulation}
                        compareMode={topologyState.compareMode}
                        onToggleCompareMode={topologyState.toggleCompareMode}
                    />
                );
            case 'simulation':
                return (
                    <SimulationConfig
                        selectedTopologies={topologyState.selectedTopologies}
                        onSimulationComplete={handleSimulationComplete}
                        onNavigateToResults={() => setStep('results')}
                    />
                );
            case 'results':
                return (
                    <ResultsView
                        result={history.simulationResult}
                        comparisonResults={history.comparisonResults}
                        selectedTopologies={topologyState.selectedTopologies}
                        storedSimulations={history.storedSimulations}
                        loadingHistory={history.loadingHistory}
                        onLoadSimulation={handleLoadSimulation}
                        onDeleteSimulation={history.handleDeleteSimulation}
                        onNavigateToSimulation={() => setStep('simulation')}
                        onNavigateToTopologies={() => setStep('topologies')}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Step breadcrumb bar */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mb: 1.5,
                flexShrink: 0,
            }}>
                {STEPS.map(({ id, label }, i) => {
                    const isActive = step === id;
                    const reachable = canGoTo(id);
                    return (
                        <Chip
                            key={id}
                            label={label}
                            size="small"
                            onClick={reachable ? () => setStep(id) : undefined}
                            sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                fontWeight: isActive ? 700 : 500,
                                letterSpacing: '0.04em',
                                height: 26,
                                cursor: reachable ? 'pointer' : 'default',
                                bgcolor: isActive
                                    ? alpha(accentColor, 0.2)
                                    : 'rgba(255,255,255,0.04)',
                                color: isActive
                                    ? accentColor
                                    : reachable ? '#aaa' : '#555',
                                border: `1px solid ${isActive
                                    ? alpha(accentColor, 0.5)
                                    : 'rgba(255,255,255,0.08)'}`,
                                transition: 'all 0.2s ease',
                                '&:hover': reachable ? {
                                    bgcolor: alpha(accentColor, 0.12),
                                    borderColor: alpha(accentColor, 0.3),
                                } : {},
                            }}
                        />
                    );
                })}
            </Box>

            {/* Content area */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {renderContent()}
            </Box>
        </Box>
    );
}
